import * as preact from "preact";

import * as localAPI from "localAPI";
import models from "models/models";
import readlangClient from "readlangClient";
import * as dateUtils from "shared/dateUtils";
import * as languageMap from "shared/languageMap";

type Props = {
  appRouter: any;
};

type State = {
  localMode: boolean;
  importState:
    | {
        type: "waitingForFileInput";
      }
    | {
        type: "importing";
      }
    | {
        type: "importFailed";
        message: string;
      }
    | {
        type: "importSuccess";
        message: string;
      };
  csvTextData?: string;
};

export default class ImportWordsPageView extends preact.Component<
  Props,
  State
> {
  constructor(props: Props) {
    super(props);

    this.state = {
      localMode: false,
      importState: {
        type: "waitingForFileInput",
      },
    };

    this.checkLocalMode();
  }

  async checkLocalMode() {
    this.setState({
      localMode: await localAPI.isLocalMode(),
    });
  }

  render() {
    if (this.state.localMode) {
      return <div>Sign in to your account to import words.</div>;
    }

    const user = models.user;

    const learningLanguageString =
      languageMap.map[user.get("currentLearningLanguage")].english;
    const firstLanguage = languageMap.map[user.get("firstLanguage")].english;

    return (
      <div class="importWordsView">
        <h1>Import Words From CSV (BETA)</h1>

        <p>
          <strong>WARNING</strong>: This feature is in beta and for technical
          users only. The file format requirement is strict, requiring you to
          prepare your data correctly. Also, it's possible that these
          requirements could change in future.
        </p>

        <h2>File Format</h2>

        <p>
          Your CSV (comma separated values) file must have the following columns
          in the following order:
        </p>

        <ol>
          <li>
            <strong>{learningLanguageString} Word or Phrase</strong> - if you
            include <span class="inline-pre"> / </span> this will be interpreted
            as a separator between synonyms.
          </li>
          <li>
            <strong>{firstLanguage} Translation</strong> - if you include{" "}
            <span class="inline-pre"> / </span>
            this will be interpreted as a separator between alternative
            translations.
          </li>
          <li>
            <strong>Context</strong> (optional, but strongly recommended) - must
            contain the exact form of the {learningLanguageString} word or
            phrase provided in column 1. If you have included multiple synonyms
            using <span class="inline-pre"> / </span> delimiters in column 1, it
            must match the first one.
          </li>
          <li>
            <strong>Practice Interval</strong> (optional) - in number of days.
          </li>
          <li>
            <strong>Next Practice Date</strong> (optional) - in{" "}
            <span class="inline-pre">YYYY-MM-DD</span> format, e.g.{" "}
            <span class="inline-pre">2024-08-24</span>. If this is omitted, it
            will be scheduled for the day you import.
          </li>
        </ol>

        <p>
          Multi-line contexts are not supported and newlines are not allowed
          within any cells.
        </p>

        <p>
          Don't include a header row, start the data on the first line of your
          CSV file.
        </p>

        <p>
          Note that the <span class="inline-pre"> / </span> delimiter to
          distinguish between synonyms and translations contains spaces around
          the forward slash character.
        </p>

        <p>
          If existing words already exist in your account, they will be merged
          with the new words, keeping all synonyms, alternative translations and
          contexts of both. If you provide spaced repetition intervals and next
          practice dates, the new values will overwrite the old values.
        </p>

        <p>
          There's a limit of 200 words per file for the time being. This is to
          limit the damage in case anything goes wrong and I may increase this
          once I get more feedback that the feature is working well.
        </p>

        <h2>Example CSV file contents</h2>
        <pre class="exampleCsvFile">{this.renderCsvExample()}</pre>

        {this.renderImportView()}

        <p>
          If you have any problems or feedback related to this feature, please
          post in{" "}
          <a href="https://forum.readlang.com/t/secret-csv-word-import-feature-available/138">
            this forum thread
          </a>
          .
        </p>
      </div>
    );
  }

  renderCsvExample() {
    const data = [
      [
        "playa",
        "beach",
        "Me encanta ir a la playa durante el verano.",
        3,
        dateUtils.format(
          new Date(Date.now() + 2 * dateUtils.DAY),
          "%YYYY-%MM-%DD"
        ),
      ],
      ["sol", "sun", "El sol brilla fuerte hoy."],
      [
        "arena",
        "sand",
        "La arena está caliente bajo mis pies.",
        102,
        dateUtils.format(
          new Date(Date.now() + 62 * dateUtils.DAY),
          "%YYYY-%MM-%DD"
        ),
      ],
      ["mar", "sea", "El mar está tranquilo esta mañana, vamos a nadar!"],
      [
        "bañador",
        "swimsuit / bathing costume",
        "Compré un nuevo bañador para las vacaciones.",
      ],
      [
        "sombrilla / parasol",
        "beach umbrella / sun umbrella",
        "Pusimos la sombrilla cerca del agua.",
      ],
    ];

    return (
      <>
        {data.map((row) => (
          <>
            {row.map((cell, index) => (
              <>
                {index > 0 && ","}
                <span class={`column-${index + 1}`}>
                  {typeof cell === "string" && /,/.test(cell)
                    ? `"${cell}"`
                    : cell}
                </span>
              </>
            ))}
            <br />
          </>
        ))}
      </>
    );
  }

  async uploadCsv(contents: string) {
    const user = models.user;

    let response;
    try {
      response = await readlangClient.request("POST", "/api/wordImport", {
        csv: contents,
        language: user.get("currentLearningLanguage"),
      });
    } catch (error) {
      console.error("ERROR IMPORTING: ", error);
      this.setState({
        importState: {
          type: "importFailed",
          message: error.jqXHR.data,
        },
      });
      return;
    }

    this.setState({
      importState: {
        type: "importSuccess",
        message: `Successfully imported ${response.data.importedCount} word${
          response.data.importedCount === 1 ? "" : "s"
        }.`,
      },
    });
  }

  renderImportView() {
    switch (this.state.importState.type) {
      case "waitingForFileInput":
        return (
          <>
            <h2>
              <small>either</small> Upload CSV file
            </h2>
            <form>
              <input
                type="file"
                accept=".csv"
                onChange={async (event) => {
                  this.setState({
                    importState: {
                      type: "importing",
                    },
                  });
                  const reader = new FileReader();
                  reader.onload = (onloadEvent) => {
                    this.uploadCsv(onloadEvent.target.result.toString());
                  };
                  reader.readAsText(event.currentTarget.files[0], "UTF-8");
                }}
              >
                Choose CSV File
              </input>
            </form>
            <h2>
              <small>or</small> Paste CSV data
            </h2>
            <div>
              <textarea
                onInput={(event) =>
                  this.setState({ csvTextData: event.currentTarget.value })
                }
                value={this.state.csvTextData}
                rows={10}
              />
              <button
                disabled={!this.state.csvTextData}
                onClick={() => {
                  this.setState({
                    importState: {
                      type: "importing",
                    },
                  });
                  this.uploadCsv(this.state.csvTextData);
                }}
              >
                Upload CSV data
              </button>
            </div>
          </>
        );

      case "importing":
        return (
          <div>
            <h2>CSV Import Status</h2>
            <p>Importing...</p>
          </div>
        );

      case "importFailed":
        return (
          <div>
            <h2>CSV Import Status</h2>
            <p class="failedMessage">
              <i class="icon icon-warning-sign"></i>{" "}
              {this.state.importState.message}
            </p>
            <button
              onClick={() => {
                this.setState({
                  importState: {
                    type: "waitingForFileInput",
                  },
                });
              }}
            >
              Try Again
            </button>
          </div>
        );

      case "importSuccess":
        return (
          <div>
            <h2>CSV Import Status</h2>
            <p>✓ {this.state.importState.message}</p>
            <p>
              <a href="/words">View Words</a>
            </p>
            <p>
              <button
                onClick={() => {
                  this.setState({
                    importState: {
                      type: "waitingForFileInput",
                    },
                  });
                }}
              >
                Import more words
              </button>
            </p>
          </div>
        );
    }
  }
}
